import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export { default as Button } from './Button.vue'

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1',
    'whitespace-nowrap transition-colors cursor-pointer',
    // 不使用 disabled:pointer-events-none：pointer-events:none 会让鼠标穿透元素，
    // 导致 cursor-not-allowed 永远不生效；点击拦截由原生 button disabled 保证，
    // hover 样式的屏蔽由全局 hover variant（styles/index.css 中重定义）负责
    'disabled:cursor-not-allowed',
  ].join(' '),
  {
    variants: {
      /**
       * 颜色主题 — fill + active:false 的完整样式
       * 其他 variant/active 组合通过 compoundVariants 覆盖
       */
      color: {
        blue1:
          'bg-blue-light-default text-text-active hover:bg-blue-light-hover active:bg-blue-light-active active:text-white-black',
        blue2: 'bg-blue-dark-default text-white hover:bg-blue-dark-hover active:bg-brand-blue1',
        black:
          'bg-neutral-dark-default text-white hover:bg-neutral-dark-hover active:bg-neutral-dark-active',
        'text-b-w':
          'bg-neutral-light-default text-black-white hover:bg-neutral-light-hover hover:text-text-glass-hover active:bg-neutral-light-active active:text-black-white',
        'text-b-b':
          'bg-neutral-light-default text-text-active hover:bg-neutral-light-hover hover:text-text-hover active:bg-neutral-light-active active:text-text-active',
        normal:
          'bg-key text-black-white hover:text-text-glass-default active:bg-white active:text-brand-blue2',
        glass:
          'glass-frosted text-black-white hover:text-text-glass-hover active:bg-white active:text-black',
      },

      /**
       * 变体类型 — 结构性样式 + disabled
       */
      variant: {
        fill: 'border-none disabled:bg-neutral-dark-disabled disabled:text-text-disabled disabled:cursor-not-allowed',
        wireframe:
          'border border-1 box-border bg-transparent disabled:border-neutral-light-hover disabled:text-text-disabled disabled:cursor-not-allowed',
        frameless:
          'border-none bg-transparent disabled:text-text-disabled disabled:cursor-not-allowed',
      },

      /** 尺寸 */
      size: {
        lg: 'p-3 text-l-medium [&_svg]:size-6 not-has-[svg]:px-6',
        md: 'px-2.25 py-3 text-m-medium [&_svg]:size-5 not-has-[svg]:px-4 not-has-[svg]:py-2',
        sm: 'px-3 py-2 text-s-medium [&_svg]:size-4 not-has-[svg]:px-3',
        xs: 'py-0.5 pl-1 pr-1.5 text-s-medium [&_svg]:size-3.5 not-has-[svg]:px-2',
        // 只有图标的情况使用
        'icon-xs': '[&_svg]:size-4.5 p-0.75',
        'icon-sm': '[&_svg]:size-5.5 p-1.25',
        'icon-md': '[&_svg]:size-6.5 p-1.75',
        'icon-lg': '[&_svg]:size-8 p-2',
      },

      /** 形状 */
      shape: {
        corner: 'rounded-6xl',
        rect: 'rounded-t-l rounded-b-l',
        square: 'rounded-t-m rounded-b-m',
        circle: 'rounded-full',
      },

      /** 持久激活态 */
      active: {
        true: '',
        false: '',
      },
    },

    defaultVariants: {
      variant: 'fill',
      size: 'md',
      color: 'blue1',
      shape: 'corner',
      active: false,
    },

    compoundVariants: [
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // blue1
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // fill 激活态
      {
        color: 'blue1',
        variant: 'fill',
        active: true,
        class: 'bg-blue-light-active text-white-black hover:bg-blue-light-active',
      },
      // wireframe 常态
      {
        color: 'blue1',
        variant: 'wireframe',
        active: false,
        class:
          'text-text-body-default border-theme-light-default hover:bg-transparent hover:border-theme-light-hover hover:text-text-blue-hover active:border-theme-light-active active:bg-blue-light-default active:text-brand-blue1',
      },
      // wireframe 激活态
      {
        color: 'blue1',
        variant: 'wireframe',
        active: true,
        class:
          'border-theme-light-active bg-blue-light-default text-brand-blue1 hover:border-theme-light-active',
      },
      // frameless 常态
      {
        color: 'blue1',
        variant: 'frameless',
        active: false,
        class:
          'text-text-active hover:bg-blue-light-default active:bg-blue-light-active active:text-white-black',
      },
      // frameless 激活态
      {
        color: 'blue1',
        variant: 'frameless',
        active: true,
        class:
          'bg-blue-light-active text-white-black hover:text-white-black hover:bg-blue-light-active',
      },

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // blue2
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // fill 激活态
      {
        color: 'blue2',
        variant: 'fill',
        active: true,
        class: 'bg-brand-blue1 hover:bg-brand-blue1',
      },
      // frameless 常态
      {
        color: 'blue2',
        variant: 'frameless',
        active: false,
        class:
          'text-text-active hover:bg-blue-dark-hover hover:text-white active:bg-brand-blue1 active:text-white',
      },
      // frameless 激活态
      {
        color: 'blue2',
        variant: 'frameless',
        active: true,
        class: 'bg-brand-blue1 text-white hover:bg-brand-blue1',
      },

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // black
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // fill 激活态
      {
        color: 'black',
        variant: 'fill',
        active: true,
        class: 'bg-neutral-dark-active text-white hover:bg-neutral-dark-active',
      },
      // wireframe 常态
      {
        color: 'black',
        variant: 'wireframe',
        active: false,
        class:
          'text-black-white border-neutral-dark-default hover:bg-transparent hover:text-text-hover hover:border-neutral-dark-hover active:text-white active:border-n-dark-active active:bg-neutral-dark-active',
      },
      // wireframe 激活态
      {
        color: 'black',
        variant: 'wireframe',
        active: true,
        class:
          'text-white border-n-dark-active bg-neutral-dark-active hover:text-white hover:bg-neutral-dark-active hover:border-n-dark-active',
      },
      // frameless 常态
      {
        color: 'black',
        variant: 'frameless',
        active: false,
        class:
          'text-black-white hover:bg-neutral-dark-hover hover:text-white active:bg-neutral-dark-active active:text-white',
      },
      // frameless 激活态
      {
        color: 'black',
        variant: 'frameless',
        active: true,
        class: 'bg-neutral-dark-active text-white hover:bg-neutral-dark-active',
      },

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // text-b-w
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // fill 激活态
      {
        color: 'text-b-w',
        variant: 'fill',
        active: true,
        class:
          'bg-neutral-light-active text-black-white hover:bg-neutral-light-active hover:text-black-white',
      },
      // wireframe 常态
      {
        color: 'text-b-w',
        variant: 'wireframe',
        active: false,
        class:
          'text-black-white border-neutral-light-default1 hover:bg-transparent hover:border-neutral-light-hover hover:text-text-glass-hover active:border-n-light-active active:bg-neutral-light-active',
      },
      // wireframe 激活态
      {
        color: 'text-b-w',
        variant: 'wireframe',
        active: true,
        class:
          'text-black-white border-n-light-active bg-neutral-light-active hover:text-black-white hover:bg-neutral-light-active active:bg-neutral-light-active',
      },
      // frameless 常态
      {
        color: 'text-b-w',
        variant: 'frameless',
        active: false,
        class:
          'text-black-white hover:bg-neutral-light-default1 hover:text-text-glass-hover active:bg-neutral-light-default active:text-black-white',
      },
      // frameless 激活态
      {
        color: 'text-b-w',
        variant: 'frameless',
        active: true,
        class:
          'text-black-white bg-neutral-light-default hover:text-black-white hover:bg-neutral-light-default',
      },

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // text-b-b
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // fill 激活态
      {
        color: 'text-b-b',
        variant: 'fill',
        active: true,
        class:
          'bg-neutral-light-active text-text-active hover:bg-neutral-light-active hover:text-text-active',
      },
      // frameless 常态
      {
        color: 'text-b-b',
        variant: 'frameless',
        active: false,
        class:
          'text-text-active hover:bg-neutral-light-default1 hover:text-text-hover active:bg-neutral-light-default active:text-text-active',
      },
      // frameless 激活态
      {
        color: 'text-b-b',
        variant: 'frameless',
        active: true,
        class:
          'text-text-active bg-neutral-light-default hover:text-text-active hover:bg-neutral-light-default',
      },

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // glass（仅 fill）
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      {
        color: 'glass',
        variant: 'fill',
        active: true,
        class: 'bg-white text-black hover:bg-white hover:text-black',
      },
      {
        color: 'glass',
        variant: 'fill',
        class: 'disabled:text-text-glass-disabled disabled:glass-frosted',
      },

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // normal（仅 fill）
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      {
        color: 'normal',
        variant: 'fill',
        active: true,
        class: 'bg-white text-brand-blue2 hover:bg-white hover:text-brand-blue2',
      },
      {
        color: 'normal',
        variant: 'fill',
        class: 'disabled:text-text-glass-disabled disabled:bg-key',
      },
      {
        color: 'normal',
        size: 'sm',
        shape: 'square',
        class: 'text-xs-semibold',
      },
      {
        color: 'normal',
        size: 'xs',
        shape: 'square',
        class: 'text-xs-semibold',
      },
      {
        color: 'normal',
        size: 'sm',
        shape: 'circle',
        class: 'text-xs-semibold',
      },
      {
        color: 'normal',
        size: 'xs',
        shape: 'circle',
        class: 'text-xs-semibold',
      },
    ],
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
